import { Link as RouterLink } from 'react-router';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { MemoryOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { agentPoolsTabPath } from '@/components/agent-pools-admin/agent-pools-tabs';
import OpsDomainPanel from './ops-domain-panel';
import OpsInlineBar from './ops-inline-bar';
import OpsStackedBar from './ops-stacked-bar';
import { formatDuration } from './ops-format';
import type { OpsProcessorExhaustionDto, ProcessorOpsHealthStatus } from '@/types/dtos/ops/ops-platform.dto';

type ProcessorDomainPanelProps = {
  exhaustion: OpsProcessorExhaustionDto;
  health: ProcessorOpsHealthStatus | null;
};

export default function ProcessorDomainPanel({ exhaustion, health }: ProcessorDomainPanelProps) {
  const t = useTranslations();
  const theme = useTheme();

  const headline = exhaustion.poolEmptyWithBacklog
    ? t(
        'admin.opsAdmin.processorHeadline.poolEmptyWithBacklog',
        {
          queueDepth: exhaustion.queueDepth,
          oldest: formatDuration(exhaustion.oldestQueuedAgeSeconds),
        },
        `No connected — ${exhaustion.queueDepth} queued (oldest ${formatDuration(exhaustion.oldestQueuedAgeSeconds)})`,
      )
    : exhaustion.connected === 0 && exhaustion.enrolled > 0
      ? t(
          'admin.opsAdmin.processorAllOffline',
          { enrolled: exhaustion.enrolled },
          `All processors offline (${exhaustion.enrolled} enrolled)`,
        )
      : exhaustion.poolExhausted
        ? t(
            'admin.opsAdmin.processorHeadline.poolExhausted',
            { connected: exhaustion.connected },
            `Exhausted: 0 idle / ${exhaustion.connected} connected`,
          )
        : exhaustion.escalatedCount > 0
          ? t(
              'admin.opsAdmin.processorHeadline.escalatedJobs',
              { count: exhaustion.escalatedCount },
              `${exhaustion.escalatedCount} escalated job(s)`,
            )
          : undefined;

  const queueMax = Math.max(exhaustion.enrolled, exhaustion.queueDepth, 1);

  return (
    <OpsDomainPanel
      title={t('admin.opsAdmin.processorMetricsTitle', undefined, 'Processor pool')}
      icon={<MemoryOutlined fontSize="small" color="action" />}
      health={health}
      headline={headline}
      footer={
        <Button
          size="small"
          component={RouterLink}
          to={agentPoolsTabPath('processor')}
          sx={{ fontSize: '0.7rem', py: 0 }}
        >
          {t('admin.opsAdmin.openProcessorPool', undefined, 'Open pool')}
        </Button>
      }
    >
      <OpsStackedBar
        label={t('admin.opsAdmin.metrics.enrolled', undefined, 'Enrolled')}
        total={exhaustion.enrolled}
        segments={[
          { key: 'busy', value: exhaustion.busy, color: theme.palette.warning.main, label: 'Busy' },
          { key: 'idle', value: exhaustion.idle, color: theme.palette.primary.main, label: 'Idle' },
          { key: 'offline', value: exhaustion.offline, color: theme.palette.action.disabled, label: 'Offline' },
          { key: 'maint', value: exhaustion.maintenance, color: theme.palette.grey[500], label: 'Maint' },
        ]}
      />
      <OpsInlineBar
        label={t('admin.opsAdmin.metrics.queued', undefined, 'Queued')}
        value={exhaustion.queueDepth}
        max={queueMax}
        warnThreshold={1}
      />
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {t('admin.opsAdmin.metrics.connected', undefined, 'Connected')} {exhaustion.connected}/{exhaustion.enrolled}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {t('admin.opsAdmin.metrics.inFlight', undefined, 'In flight')} {exhaustion.inFlightCount}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {t('admin.opsAdmin.metrics.escalated', undefined, 'Escalated')} {exhaustion.escalatedCount}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {t('admin.opsAdmin.metrics.oldestQueue', undefined, 'Oldest')}{' '}
          {formatDuration(exhaustion.oldestQueuedAgeSeconds)}
        </Typography>
      </Stack>
    </OpsDomainPanel>
  );
}
